import _ from 'underscore';
import capitalize from 'underscore.string/capitalize';

import {orderUsersAndTeams} from 'nutshell-core';

import {getColorNameFromTagColor} from 'shells/colors/helpers';
import {BoxRegularIcon} from 'shells/icon/svg/box-regular-icon';
import {BoxesRegularIcon} from 'shells/icon/svg/boxes-regular-icon';

export function mapEntityType(type, singular) {
    if (!_.contains(['accounts', 'mcfxcompanies', 'contacts', 'leads', 'users'], type)) return type;

    return singular
        ? {
              accounts: 'company',
              mcfxcompanies: 'company',
              contacts: 'person',
              leads: 'lead',
              users: 'user',
          }[type]
        : {
              accounts: 'companies',
              mcfxcompanies: 'companies',
              contacts: 'people',
              leads: 'leads',
              users: 'user',
          }[type];
}

/**
 * Looks up models for the given relationships.
 * @param {Array<string>} relationships An array of API types that specify the
 *   models that will be returned.
 * @param {string} entityType The type of entity to consider when loading the
 *   'tags' relationship. If there is no 'tags' relationship, this is an unused
 *   variable.
 * @param {string} fieldName The name of the field being considered. Not used if
 *   there is no 'tags' relationship.
 * @returns {Array<Object>} An array of models.
 */
export function getFormModelsFromRelationships(relationships, entityType, fieldName) {
    const rels = relationships || [];

    const models = rels
        .reduce((list, col) => {
            const collectionName = capitalize(col);
            const collection = Nut.managedCollections[collectionName];

            // Ensure that we have a managed collection for this type
            if (!collection) {
                return [];
            }

            const items = collection.models;

            // Code below is to ensure we only show the correct tagType
            // for the type of tag filter we're applying
            let filteredItems = items.filter((item) => {
                if (item.get('isHiddenFromFilters')) return false;

                // If we're not dealing with tags, return true
                if (collectionName !== 'Tags') return true;

                // If we're filtering accountTags on people, we only want
                // account tags
                if (fieldName === 'accountTags') {
                    return item.get('tagType') === 'accounts';
                } else if (fieldName === 'contactTags') {
                    return item.get('tagType') === 'contacts';
                }

                // Otherwise, only match entityType
                return entityType === item.get('tagType');
            });

            if (fieldName === 'inProgressEmailSequenceTemplates') {
                filteredItems = filteredItems.filter((item) => {
                    return item.get('numEmails') > 1;
                });
            }

            return list.concat(filteredItems);
        }, [])
        .map((model) => {
            const modelType = model.get('type');

            let colorName;
            let icon;
            switch (modelType) {
                case 'tags':
                    colorName = getColorNameFromTagColor(model.get('colorType'));
                    break;
                case 'products':
                    icon = BoxRegularIcon;
                    break;
                case 'productCategories':
                    icon = BoxesRegularIcon;
            }

            return {
                id: model.id,
                type: modelType,
                name: model.get('name'),
                description: getDescription(model),
                isEnabled: getIsEnabled(model),
                colorName,
                icon,
            };
        });

    if (relationships.includes('users')) {
        return orderUsersAndTeams(models);
    }

    return models;
}

const getIsEnabled = (model) => {
    if (model.type !== 'users') {
        return true;
    }

    return model.get('isEnabled');
};

const getDescription = (model) => {
    if (model.type !== 'sources') {
        return '';
    }

    return model.getDescription();
};
